import { useAtomValue } from 'jotai';
import { FileWithPath, useDropzone } from 'react-dropzone';
import lzSCommonsLastErrorMessageAtom from '@lz-atoms/Commons/lastErrorMessage';
import useLzFApiCall from '@lz-services/Api';
import { useLzEffectAfterMount, useLzOnMount } from '@lz-services/Hooks';
import { LzTFunctionSimple, LzTOpenObject } from '@lz-types/types';
import LzMBase from '@lz-models/commons/Base';
import LzMMedia from '@lz-models/commons/Media';

export interface LzPMediaQuickUploader {
    stealth?: boolean;
    isPrivate?: boolean;
    multiple?: boolean;
    category?: string;
    accept?: string | Array<string>;
    minSize?: number;
    maxSize?: number;
    maxFiles?: number;
    onUploaded: (medias: Array<LzMBase>, filesErrors: Array<string>) => void;
    onCancel?: LzTFunctionSimple;
}

const LzXMediaQuickUploader = (props: LzPMediaQuickUploader) => {
    const lastErrorMessageState = useAtomValue(lzSCommonsLastErrorMessageAtom);

    const { getRootProps, getInputProps, open, acceptedFiles, isFileDialogActive } = useDropzone({
        noClick: true,
        noKeyboard: true,
        accept: LzMMedia.getDropzoneAcceptParam(props.accept),
        minSize: props.minSize,
        maxSize: props.maxSize,
        maxFiles: props.maxFiles || 1,
        multiple: !!props.multiple,
    });

    const lzApiCall = useLzFApiCall();

    useLzOnMount(() => {
        open();
    });

    useLzEffectAfterMount(() => {
        // console.log('file dialog changes', isFileDialogActive, acceptedFiles);
        if (isFileDialogActive) {
            // no change
        } else {
            if (acceptedFiles.length > 0) {
                saveSelectedFiles(acceptedFiles);
            } else {
                if (props.onCancel) {
                    props.onCancel();
                }
            }
        }
    }, [isFileDialogActive, acceptedFiles]);

    /**
     * Upload a single file to server.
     *
     * @param file File object for the file to upload.
     *
     * @returns Either the data returned from server, or false on failure.
     */
    const uploadFile = async (file: File) => {
        // Make the api call to upload the file
        const data = await lzApiCall(
            'media',
            {
                file: file,
                category: props.category || 'general',
                is_private: !!props.isPrivate,
            },
            'post',
        );

        // Load data if call was successful
        if (data) {
            return data;
        }

        return false;
    };

    /**
     * Save selected files on server.
     *
     * @param files Selected files to upload on server
     */
    const saveSelectedFiles = async (files: readonly FileWithPath[]) => {
        const medias: LzMMedia[] = [];
        const filesErrors: string[] = [];

        // Go through all files to process
        let i = 0;
        for (const file of files) {
            i++;

            // Try to upload file and get media in return
            const media = await uploadFile(file);
            console.log('UPLOAD-RESULT', media, lastErrorMessageState);

            // If failed then error message is returned as string
            if (typeof media === 'boolean') {
                filesErrors.push('File # ' + i + ': ' + lastErrorMessageState);
            }

            // If it was not an error then push to medias
            else {
                medias.push(new LzMMedia(media));
            }
        }

        if (props.onUploaded) {
            props.onUploaded(medias, filesErrors);
        }
    };

    // Build styles
    const styles: LzTOpenObject = {};
    if (props.stealth) {
        styles.display = 'none';
    }

    // Render dropzone
    return (
        <div className="container" style={styles}>
            <div {...getRootProps({ className: 'dropzone' })}>
                <input {...getInputProps()} />
                <p>Drag 'n' drop some files here</p>
            </div>
        </div>
    );
};

export default LzXMediaQuickUploader;
